/**
 * SS-14 (#1716): 商談なし有料化率。
 *
 * 「Founder と商談せずに、無料体験 → 有料化した会社の割合」を週次（直近 8 週）と累計で見る
 * （Growth Canon §44、正本 docs/web/analytics-plan.md §6）。
 * - 率 = 相談なし ÷（相談なし + 相談あり）。**未確認は分母から外し、件数を別の列で出す**
 * - 相談の有無は Founder がこの画面で確定する。候補（alys の 15分相談フォームの相談とドメインが一致）は
 *   ドメインと件数だけを出す（メールアドレス・氏名は出さない。Founder 決定 2026-09-23）
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ConsultStatus,
  type ConversionBucket,
  type ConversionRow,
  fetchConversion,
  setConsultStatus,
} from '../../lib/conversion-api';

export interface ConversionPageProps {
  apiBaseUrl: string;
}

const QUERY_KEY = ['admin-ops-conversion'] as const;

const STATUS_LABEL: Record<ConsultStatus, string> = {
  consulted: '相談あり',
  no_consult: '相談なし',
  unconfirmed: '未確認',
};

const formatRate = (rate: number | null) => (rate === null ? '—' : `${Math.round(rate * 100)}%`);

/** ISO → JST の YYYY-MM-DD。 */
const jstDate = (iso: string) =>
  new Date(new Date(iso).getTime() + 9 * 3_600_000).toISOString().slice(0, 10);

function Cumulative({ bucket }: { bucket: ConversionBucket }) {
  return (
    <div
      data-testid="conversion-cumulative"
      className="flex flex-wrap items-baseline gap-x-6 gap-y-1 rounded-lg border border-slate-200 bg-white p-4"
    >
      <div>
        <p className="text-xs text-slate-500">累計の商談なし有料化率</p>
        <p className="text-2xl font-semibold tabular-nums text-slate-900">
          {formatRate(bucket.rate)}
        </p>
      </div>
      <p className="text-sm tabular-nums text-slate-600">
        有料化 {bucket.paid} / 相談なし {bucket.noConsult} / 相談あり {bucket.consulted} / 未確認{' '}
        {bucket.unconfirmed}
      </p>
      <p className="basis-full text-xs text-slate-500">
        率は相談の有無を確定した会社だけで計算します（未確認は分母に入れません）。
      </p>
    </div>
  );
}

function StatusButtons({
  row,
  onChange,
  disabled,
}: {
  row: ConversionRow;
  onChange: (status: ConsultStatus) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {(['no_consult', 'consulted'] as const).map((s) => (
        <button
          key={s}
          type="button"
          aria-pressed={row.status === s}
          disabled={disabled}
          onClick={() => onChange(s)}
          className={
            row.status === s
              ? 'rounded border border-slate-900 bg-slate-900 px-2 py-0.5 text-xs text-white'
              : 'rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-100'
          }
        >
          {STATUS_LABEL[s]}
        </button>
      ))}
      {row.status !== 'unconfirmed' ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange('unconfirmed')}
          className="rounded px-2 py-0.5 text-xs text-slate-500 underline-offset-2 hover:underline"
        >
          未確認に戻す
        </button>
      ) : null}
    </div>
  );
}

export function ConversionPage({ apiBaseUrl }: ConversionPageProps) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: () => fetchConversion(apiBaseUrl) });
  const mutation = useMutation({
    mutationFn: ({ workspaceId, status }: { workspaceId: string; status: ConsultStatus }) =>
      setConsultStatus(apiBaseUrl, workspaceId, status),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
  const data = query.data;

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-7xl px-6 py-8">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-wider text-slate-500">
            ALYS Internal / admin-ops
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">商談なし有料化率</h1>
          <p className="mt-2 text-sm text-slate-600">
            SS-14: Founder と
            15分相談・商談をせずに、無料体験から有料化した会社の割合。相談の有無は下の一覧で確定します。
            候補は alys の
            15分相談フォームの相談とドメインが一致したものです（確定の参考。自動では決めません）。
          </p>
        </header>

        {query.isLoading && <p className="text-sm text-slate-500">読み込み中…</p>}
        {query.isError && (
          <p className="text-sm text-rose-600">
            API 取得に失敗しました: {(query.error as Error).message}
          </p>
        )}
        {data && !data.configured && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            admin-ops-api の商談なし有料化率が未配線です（製品 DB の読み取り、または admin-ops DB
            の接続が未設定）。
          </p>
        )}
        {data?.configured && (
          <div className="flex flex-col gap-6">
            <Cumulative bucket={data.cumulative} />

            <section>
              <h2 className="mb-2 text-sm font-semibold text-slate-700">
                週次（直近 8 週、月曜始まり）
              </h2>
              <table
                aria-label="週次の商談なし有料化率"
                className="w-full overflow-hidden rounded-lg border border-slate-200 bg-white text-sm"
              >
                <thead className="bg-slate-100 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2">週</th>
                    <th className="px-3 py-2">有料化</th>
                    <th className="px-3 py-2">相談なし</th>
                    <th className="px-3 py-2">相談あり</th>
                    <th className="px-3 py-2">未確認（分母外）</th>
                    <th className="px-3 py-2">率</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {[...data.weeks].reverse().map((w) => (
                    <tr key={w.weekStart} className="border-t border-slate-100">
                      <td className="px-3 py-2">{w.weekStart}</td>
                      <td className="px-3 py-2">{w.paid}</td>
                      <td className="px-3 py-2">{w.noConsult}</td>
                      <td className="px-3 py-2">{w.consulted}</td>
                      <td className="px-3 py-2">{w.unconfirmed}</td>
                      <td className="px-3 py-2">{formatRate(w.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section>
              <h2 className="mb-2 text-sm font-semibold text-slate-700">
                有料化した会社（相談の有無を確定する）
              </h2>
              {mutation.isError && (
                <p className="mb-2 text-sm text-rose-600">
                  確定に失敗しました: {(mutation.error as Error).message}
                </p>
              )}
              {data.rows.length === 0 ? (
                <p className="text-sm text-slate-500">まだ有料化した会社はありません。</p>
              ) : (
                <table className="w-full overflow-hidden rounded-lg border border-slate-200 bg-white text-sm">
                  <thead className="bg-slate-100 text-left text-xs text-slate-500">
                    <tr>
                      <th className="px-3 py-2">会社</th>
                      <th className="px-3 py-2">有料化日</th>
                      <th className="px-3 py-2">相談の候補</th>
                      <th className="px-3 py-2">状態</th>
                      <th className="px-3 py-2">確定</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr key={row.workspaceId} className="border-t border-slate-100 align-top">
                        <td className="px-3 py-2">{row.displayName}</td>
                        <td className="px-3 py-2 tabular-nums">{jstDate(row.paidAt)}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.candidate
                            ? `${row.candidate.domain} の相談 ${row.candidate.count} 件（最新 ${jstDate(row.candidate.latestAt)}）`
                            : '候補なし'}
                        </td>
                        <td className="px-3 py-2">{STATUS_LABEL[row.status]}</td>
                        <td className="px-3 py-2">
                          <StatusButtons
                            row={row}
                            disabled={mutation.isPending}
                            onChange={(status) =>
                              mutation.mutate({ workspaceId: row.workspaceId, status })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        )}
      </div>
    </main>
  );
}
